import { useCallback, useEffect, useRef, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import type { GraphData, GraphNode, GraphEdge, ResourceType } from '@shared/types';
import { getRelationships } from '../lib/api-client';
import type { ApiResponse } from '../lib/api-client';
import { cn } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Share2, Loader2, AlertTriangle } from 'lucide-react';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface GraphFilters {
  provider: string;
  type: string;
  project: string;
}

interface ForceGraphNode {
  id: string;
  name: string;
  type: ResourceType;
  provider: 'gcp' | 'aws';
}

interface ForceGraphLink {
  source: string;
  target: string;
  type: string;
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const PROVIDER_OPTIONS = [
  { value: '', label: 'All Providers' },
  { value: 'gcp', label: 'GCP' },
  { value: 'aws', label: 'AWS' },
];

const RESOURCE_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Types' },
  { value: 'vpc', label: 'VPC' },
  { value: 'subnet', label: 'Subnet' },
  { value: 'firewall_rule', label: 'Firewall Rule' },
  { value: 'security_group', label: 'Security Group' },
  { value: 'load_balancer', label: 'Load Balancer' },
  { value: 'vpn_tunnel', label: 'VPN Tunnel' },
  { value: 'vpn_connection', label: 'VPN Connection' },
  { value: 'transit_gateway', label: 'Transit Gateway' },
  { value: 'nat_gateway', label: 'NAT Gateway' },
  { value: 'psc_endpoint', label: 'PSC Endpoint' },
  { value: 'peering', label: 'Peering' },
  { value: 'dns_zone', label: 'DNS Zone' },
  { value: 'route_table', label: 'Route Table' },
  { value: 'cloud_router', label: 'Cloud Router' },
  { value: 'partner_interconnect', label: 'Partner Interconnect' },
  { value: 'direct_connect_connection', label: 'Direct Connect' },
  { value: 'ncc_hub', label: 'NCC Hub' },
  { value: 'cloud_armor_policy', label: 'Cloud Armor' },
];

const NODE_COLORS: Record<string, string> = {
  gcp: '#4285F4',
  aws: '#FF9900',
};

// ─── Component ─────────────────────────────────────────────────────────────────

export function RelationshipGraph() {
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [cachedAt, setCachedAt] = useState<string | null>(null);
  const [filters, setFilters] = useState<GraphFilters>({
    provider: '',
    type: '',
    project: '',
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });

  // Resize observer for responsive graph
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setDimensions({
          width: Math.max(width, 400),
          height: Math.max(height, 400),
        });
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Fetch graph data
  useEffect(() => {
    async function fetchGraph() {
      setLoading(true);
      setError(null);
      try {
        const params: Record<string, string | undefined> = {
          provider: filters.provider || undefined,
          type: filters.type || undefined,
          project: filters.project || undefined,
        };

        const response: ApiResponse<GraphData> = await getRelationships(params);
        setGraphData(response.data);
        setStale(response.stale);
        setCachedAt(response.cachedAt);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load relationship graph');
      } finally {
        setLoading(false);
      }
    }
    fetchGraph();
  }, [filters]);

  // Transform GraphData to force-graph format
  const forceGraphData = useCallback(() => {
    if (!graphData) return { nodes: [], links: [] };

    const nodes: ForceGraphNode[] = graphData.nodes.map((node: GraphNode) => ({
      id: node.id,
      name: node.name,
      type: node.type,
      provider: node.provider,
    }));

    const links: ForceGraphLink[] = graphData.edges.map((edge: GraphEdge) => ({
      source: edge.source,
      target: edge.target,
      type: edge.type,
    }));

    return { nodes, links };
  }, [graphData])();

  // Node canvas rendering: show name + type
  const nodeCanvasObject = useCallback(
    (node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
      const label = node.name || node.id;
      const typeLabel = (node.type || '').replace(/_/g, ' ');
      const fontSize = 12 / globalScale;
      const typeFontSize = 10 / globalScale;
      const nodeRadius = 6 / globalScale;

      // Draw node circle
      ctx.beginPath();
      ctx.arc(node.x, node.y, nodeRadius, 0, 2 * Math.PI);
      ctx.fillStyle = NODE_COLORS[node.provider] || '#6B7280';
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1.5 / globalScale;
      ctx.stroke();

      // Draw name label
      ctx.font = `${fontSize}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = '#1F2937';
      ctx.fillText(label, node.x, node.y + nodeRadius + 2 / globalScale);

      // Draw type label below name
      ctx.font = `${typeFontSize}px sans-serif`;
      ctx.fillStyle = '#6B7280';
      ctx.fillText(typeLabel, node.x, node.y + nodeRadius + 2 / globalScale + fontSize + 1 / globalScale);
    },
    []
  );

  // Filter change handlers
  const handleFilterChange = (key: keyof GraphFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // ─── Render ────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="h-12 w-12 rounded-full border-4 border-gray-100" />
            <Loader2 className="absolute inset-0 h-12 w-12 animate-spin text-primary-500" />
          </div>
          <p className="text-sm font-medium text-gray-400">Cargando grafo de relaciones…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200/50 bg-gradient-to-r from-red-50 to-white">
        <CardContent className="flex items-center gap-4 p-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle className="h-5 w-5 text-red-600" />
          </div>
          <p className="text-sm text-red-700">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex h-full flex-col space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-violet-600 shadow-md shadow-violet-500/20">
            <Share2 className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900">Relaciones</h1>
            <p className="text-sm text-gray-500">
              Grafo visual de relaciones entre recursos cloud.
            </p>
          </div>
        </div>
        {stale && (
          <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5">
            <div className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-xs font-medium text-amber-700">Datos en caché</span>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card className="overflow-hidden">
        <CardContent className="flex flex-wrap items-end gap-4 p-4">
          {/* Provider Filter */}
          <div className="flex flex-col">
            <label htmlFor="filter-provider" className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              Provider
            </label>
            <select
              id="filter-provider"
              value={filters.provider}
              onChange={(e) => handleFilterChange('provider', e.target.value)}
              className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 transition-all focus:border-primary-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            >
              {PROVIDER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Resource Type Filter */}
          <div className="flex flex-col">
            <label htmlFor="filter-type" className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              Resource Type
            </label>
            <select
              id="filter-type"
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 transition-all focus:border-primary-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            >
              {RESOURCE_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Project/Account Filter */}
          <div className="flex flex-col">
            <label htmlFor="filter-project" className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              Project / Account
            </label>
            <input
              id="filter-project"
              type="text"
              value={filters.project}
              onChange={(e) => handleFilterChange('project', e.target.value)}
              placeholder="Enter project or account ID"
              className="w-56 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 transition-all focus:border-primary-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            />
          </div>
        </CardContent>
      </Card>

      {/* Truncation Warning */}
      {graphData?.truncated && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
          <p className="text-xs font-medium text-amber-700">
            Mostrando {graphData.nodes.length} de {graphData.totalNodes} nodos. Usa filtros para reducir el grafo.
          </p>
        </div>
      )}

      {/* Graph Container */}
      <div
        ref={containerRef}
        className="flex-1 min-h-[500px] rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
      >
        {forceGraphData.nodes.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <p className="text-sm text-gray-500">
              No relationships found. Try adjusting the filters.
            </p>
          </div>
        ) : (
          <ForceGraph2D
            graphData={forceGraphData}
            nodeCanvasObject={nodeCanvasObject}
            nodeLabel={(node: any) => `${node.name} (${(node.type || '').replace(/_/g, ' ')})`}
            linkLabel={(link: any) => (link.type || '').replace(/_/g, ' ')}
            linkDirectionalArrowLength={4}
            linkDirectionalArrowRelPos={1}
            nodeAutoColorBy="provider"
            width={dimensions.width}
            height={dimensions.height}
          />
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: NODE_COLORS.gcp }} />
          <span>GCP</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: NODE_COLORS.aws }} />
          <span>AWS</span>
        </div>
        {graphData && (
          <span className="ml-auto">
            {graphData.nodes.length} nodes · {graphData.edges.length} edges
          </span>
        )}
      </div>
    </div>
  );
}
